<template>
  <div class="favorites-page">
    <!-- 已下线提示横幅 -->
    <div class="offline-bar" v-if="invalidCount > 0 && !managing" @click="openInvalidPopup">
      <div class="offline-bar-content">
        <img :src="volumeIcon" class="offline-bar-icon" alt="" />
        <span class="offline-bar-text">{{ $tc("notice.favorite_offline_tip", invalidCount, { count: invalidCount }) }}</span>
        <img :src="arrowIcon" class="offline-bar-arrow" alt="" />
      </div>
    </div>
    <div class="favorites-content">
      <!-- 管理 / 清空收藏夹 -->
      <div class="favorites-toolbar" v-if="favorites.length > 0">
        <template v-if="!managing">
          <span class="toolbar-action" @click="managing = true">{{ $t("notice.manage") }}</span>
        </template>
        <template v-else>
          <span class="toolbar-action toolbar-action-primary" @click="onClearFavorites">{{ $t("notice.clear_favorites") }}</span>
          <span class="toolbar-sep">｜</span>
          <span class="toolbar-action toolbar-action-primary" @click="managing = false">{{ $t("notice.done") }}</span>
        </template>
      </div>
      <!-- 收藏列表 -->
      <div class="favorites-list">
        <div
          v-for="item in favorites"
          :key="item.favorite_id"
          class="msg-card"
          :class="{ 'msg-card-active': actionPopover.visible && actionPopover.item && actionPopover.item.favorite_id === item.favorite_id }"
          @click="clickFavorite(item)"
          @touchstart="onCardTouchStart($event, item)"
          @touchmove.passive="onCardTouchMove"
          @touchend="onCardTouchEnd"
          @touchcancel="onCardTouchEnd"
          @contextmenu.prevent
        >
          <div class="msg-header">
            <van-tag :class="item.source === 'notice' ? 'tag-notice' : 'tag-dm'">{{ msgTagText(item) }}</van-tag>
            <img v-if="managing" :src="deleteIcon" class="msg-delete-icon" alt="" @click.stop="removeFavoriteItem(item)" />
          </div>
          <div class="msg-title">{{ item.title }}</div>
          <div class="msg-time">{{ item.publish_at_ago }}</div>
          <div class="msg-author">
            <van-image v-if="item.publisher" round width="24px" height="24px" :src="item.publisher.photoUrl" class="avatar" />
            <div class="author-name">{{ item.publisher && item.publisher.name }}</div>
            <div class="author-sep" v-if="item.sign_title">｜</div>
            <div class="author-title" v-if="item.sign_title">{{ item.sign_title }}</div>
          </div>
        </div>
        <div class="favorites-loading" v-if="loading"><van-loading size="24" /></div>
        <van-empty :description="$t('notice.favorite_tip')" v-if="!loading && favorites.length === 0" />
      </div>
    </div>
    <!-- 长按操作气泡 -->
    <div
      v-if="actionPopover.visible"
      class="action-popover"
      :style="{ left: actionPopover.x + 'px', top: actionPopover.y + 'px' }"
      @click.stop
      @touchstart.stop
    >
      <div class="action-popover-body" @click="removeFromFavorites">{{ $t("notice.unfavorite") }}</div>
      <img :src="popoverArrowIcon" class="action-popover-arrow" alt="" />
    </div>
    <!-- 已下线弹窗 -->
    <van-popup v-model="showInvalidPopup" position="bottom" round class="invalid-popup">
      <div class="invalid-popup-header">
        <span class="invalid-popup-title">{{ $t("notice.offline") }}</span>
        <img :src="closeIcon" class="invalid-popup-close" alt="" @click="showInvalidPopup = false" />
      </div>
      <div class="invalid-popup-list">
        <div v-for="item in invalidItems" :key="item.favorite_id" class="invalid-cell">
          <div class="invalid-cell-body">
            <van-tag :class="item.source === 'notice' ? 'tag-notice' : 'tag-dm'">{{ msgTagText(item) }}</van-tag>
            <div class="invalid-cell-title">{{ item.title || $t("notice.message_deleted") }}</div>
          </div>
          <div class="invalid-cell-line"></div>
        </div>
        <div class="invalid-popup-loading" v-if="invalidLoading"><van-loading size="24" /></div>
        <van-empty :description="$t('global.empty')" v-if="!invalidLoading && invalidItems.length === 0" />
      </div>
      <div class="invalid-popup-footer">
        <div class="invalid-clear-btn" @click="onClearInvalid">{{ $t("notice.unfavorite") }}</div>
      </div>
    </van-popup>
  </div>
</template>

<script>
import Vue from "vue";
import { Tag, Popup, Loading, Empty, Dialog, Toast, Image as VanImage } from "vant";
import { favoritesList, invalidFavoritesList, removeFavorite, clearFavorites, clearInvalidFavorites } from "@/api/searchNotice";
import volumeIcon from "@/assets/icon_volume_o.svg";
import arrowIcon from "@/assets/icon_arrow_orange.svg";
import closeIcon from "@/assets/icon_close.svg";
import popoverArrowIcon from "@/assets/icon_popover_arrow.svg";
import deleteIcon from "@/assets/icon_trash.svg";

Vue.use(Tag);
Vue.use(Popup);
Vue.use(Loading);
Vue.use(Empty);
Vue.use(Dialog);
Vue.use(Toast);
Vue.use(VanImage);

export default {
  name: "SearchFavorites",
  data() {
    return {
      volumeIcon,
      arrowIcon,
      closeIcon,
      popoverArrowIcon,
      deleteIcon,
      favorites: [],
      total: 0,
      invalidCount: 0,
      loading: false,
      managing: false,
      actionPopover: { visible: false, x: 0, y: 0, item: null },
      longPressTimer: null,
      longPressStart: null,
      longPressMoved: false,
      suppressClick: false,
      showInvalidPopup: false,
      invalidItems: [],
      invalidLoading: false,
    };
  },
  created() {
    this.loadFavorites();
  },
  mounted() {
    document.addEventListener("click", this.onDocumentClick);
  },
  beforeDestroy() {
    document.removeEventListener("click", this.onDocumentClick);
    clearTimeout(this.longPressTimer);
  },
  methods: {
    loadFavorites() {
      this.loading = true;
      favoritesList()
        .then((res) => {
          this.loading = false;
          if (res.code !== 0) return;
          const data = res.data || {};
          this.favorites = data.items || [];
          this.total = Number(data.total) || 0;
          this.invalidCount = Number(data.invalid_count) || 0;
        })
        .catch(() => {
          this.loading = false;
        });
    },
    msgTagText(item) {
      if (!item) return "";
      if (item.source === "notice") return this.$t("notice.notice");
      const map = { teacher: this.$t("notice.direct_message") };
      return map[item.type] || item.type || this.$t("journal.normal_journal");
    },
    clickFavorite(item) {
      if (this.managing) return;
      if (this.suppressClick) {
        this.suppressClick = false;
        return;
      }
      if (!item || !item.id) return;
      if (item.source === "notice") {
        this.$router.push({ path: `/notice/${item.id}` });
      } else if (item.source === "journal") {
        this.$router.push({ path: `/journal/${item.id}` });
      }
    },
    onDocumentClick() {
      this.hideActionPopover();
      this.suppressClick = false;
    },
    onCardTouchStart(event, item) {
      if (this.managing) return;
      this.hideActionPopover();
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      this.longPressStart = { x: touch.clientX, y: touch.clientY };
      this.longPressMoved = false;
      clearTimeout(this.longPressTimer);
      this.longPressTimer = setTimeout(() => {
        if (this.longPressMoved) return;
        this.suppressClick = true;
        this.actionPopover = {
          visible: true,
          x: touch.clientX,
          y: touch.clientY - 8,
          item: item,
        };
      }, 500);
    },
    onCardTouchMove(event) {
      if (!this.longPressStart) return;
      const touch = event.touches && event.touches[0];
      if (!touch) return;
      if (Math.abs(touch.clientX - this.longPressStart.x) > 10 || Math.abs(touch.clientY - this.longPressStart.y) > 10) {
        this.longPressMoved = true;
        clearTimeout(this.longPressTimer);
      }
    },
    onCardTouchEnd() {
      clearTimeout(this.longPressTimer);
      this.longPressStart = null;
    },
    hideActionPopover() {
      clearTimeout(this.longPressTimer);
      this.actionPopover = { visible: false, x: 0, y: 0, item: null };
    },
    removeFromFavorites() {
      this.removeFavoriteItem(this.actionPopover.item);
    },
    removeFavoriteItem(item) {
      if (!item || !item.favorite_id) return;
      removeFavorite(item.favorite_id)
        .then((res) => {
          this.hideActionPopover();
          if (res.code !== 0) return;
          this.favorites = this.favorites.filter((f) => f.favorite_id !== item.favorite_id);
          this.total = Math.max(0, this.total - 1);
          if (this.favorites.length === 0) this.managing = false;
          sessionStorage.setItem("favoritesChanged", "1");
          Toast(this.$t("notice.favorite_removed"));
        })
        .catch(() => {
          this.hideActionPopover();
        });
    },
    onClearFavorites() {
      if (this.favorites.length === 0) return;
      Dialog.confirm({
        message: this.$t("notice.clear_favorites_confirm"),
      })
        .then(() => {
          clearFavorites().then((res) => {
            if (res.code !== 0) return;
            this.favorites = [];
            this.total = 0;
            this.managing = false;
            sessionStorage.setItem("favoritesChanged", "1");
            Toast(this.$t("notice.favorite_removed"));
            this.loadFavorites();
          });
        })
        .catch(() => {});
    },
    openInvalidPopup() {
      if (this.invalidLoading) return;
      // 先请求数据再弹窗，避免弹窗打开后内容高度变化导致跳动
      this.invalidLoading = true;
      invalidFavoritesList()
        .then((res) => {
          this.invalidLoading = false;
          if (res.code !== 0) return;
          this.invalidItems = (res.data && res.data.items) || [];
          this.showInvalidPopup = true;
        })
        .catch(() => {
          this.invalidLoading = false;
        });
    },
    onClearInvalid() {
      if (this.invalidItems.length === 0) return;
      clearInvalidFavorites().then((res) => {
        if (res.code !== 0) return;
        this.showInvalidPopup = false;
        this.invalidItems = [];
        sessionStorage.setItem("favoritesChanged", "1");
        Toast(this.$t("notice.favorite_removed"));
        this.loadFavorites();
      });
    },
  },
};
</script>

<style lang="less" scoped>
.favorites-page {
  min-height: 100vh;
  background: #f7f8fa;
}

.offline-bar {
  padding: 10px 16px;
  background: #fdf0e6;

  .offline-bar-content {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .offline-bar-icon {
    display: block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }

  .offline-bar-text {
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    font-size: 14px;
    line-height: 20px;
    color: #ed6a0c;
  }

  .offline-bar-arrow {
    display: block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
  }
}

.favorites-content {
  padding: 0 12px 12px;
}

.favorites-toolbar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  padding: 12px 0;
  font-size: 14px;
  line-height: 20px;
  color: #323233;

  .toolbar-action {
    cursor: pointer;
  }

  .toolbar-action-primary {
    color: #1989fa;
  }

  .toolbar-sep {
    margin: 0 8px;
    font-size: 12px;
    color: #dcdee0;
  }
}

.favorites-list {
  .msg-card {
    position: relative;
    overflow: hidden;
    background: #fff;
    border-radius: 12px;
    margin-bottom: 12px;
    padding: 16px;
    -webkit-touch-callout: none;
    -webkit-user-select: none;
    user-select: none;

    &-active::after {
      content: "";
      position: absolute;
      inset: 0;
      background: rgba(50, 50, 51, 0.08);
      pointer-events: none;
    }
  }

  .msg-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding-bottom: 10px;
    padding-right: 4px;
    border-bottom: 1px solid #ebedf0;
  }

  .msg-delete-icon {
    display: block;
    flex-shrink: 0;
    width: 16px;
    height: 16px;
    cursor: pointer;
  }

  .msg-title {
    font-size: 16px;
    font-weight: 500;
    color: #323233;
    line-height: 22px;
    margin-top: 10px;
  }

  .msg-time {
    font-size: 12px;
    color: #969799;
    line-height: 18px;
    margin-top: 2px;
  }

  .msg-author {
    display: flex;
    align-items: center;
    margin-top: 10px;

    .avatar {
      flex-shrink: 0;
      margin-right: 8px;
    }

    .author-name {
      font-size: 14px;
      color: #646566;
      line-height: 24px;
      white-space: nowrap;
    }

    .author-sep {
      font-size: 12px;
      color: #c8c9cc;
      line-height: 20px;
      margin: 0 4px;
    }

    .author-title {
      font-size: 12px;
      color: #969799;
      line-height: 20px;
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      flex: 1;
    }
  }

  .favorites-loading {
    text-align: center;
    padding: 12px 0;
  }
}

.tag-notice,
.tag-dm {
  font-size: 11px;
}

.tag-notice {
  background-color: #e8f3ff;
  color: #1989fa;
}

.tag-dm {
  background-color: #fef3e3;
  color: #f5a429;
}

.action-popover {
  position: fixed;
  z-index: 3000;
  display: flex;
  flex-direction: column;
  align-items: center;
  transform: translate(-50%, -100%);

  .action-popover-body {
    padding: 12px 16px;
    background: #4a4a4a;
    border-radius: 8px;
    font-size: 14px;
    line-height: 20px;
    color: #fff;
    white-space: nowrap;
  }

  .action-popover-arrow {
    display: block;
    width: 12px;
    height: 6px;
    margin-top: -1px;
    transform: rotate(180deg);
  }
}

.invalid-popup {
  overflow: hidden;

  .invalid-popup-header {
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    height: 54px;
  }

  .invalid-popup-title {
    font-size: 16px;
    font-weight: 500;
    line-height: 22px;
    color: #323233;
  }

  .invalid-popup-close {
    position: absolute;
    top: 50%;
    right: 16px;
    display: block;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
  }

  .invalid-popup-list {
    max-height: 60vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding-bottom: 16px;
  }

  .invalid-cell {
    position: relative;
    padding: 12px 16px;
  }

  .invalid-cell-body {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 4px;
  }

  .invalid-cell-title {
    font-size: 14px;
    line-height: 20px;
    color: #646566;
    word-break: break-word;
  }

  .invalid-cell-line {
    position: absolute;
    left: 16px;
    right: 16px;
    bottom: 0;
    height: 0.5px;
    background: #ebedf0;
  }

  .invalid-popup-footer {
    display: flex;
    padding: 8px 16px calc(8px + env(safe-area-inset-bottom));
    background: #fff;
  }

  .invalid-clear-btn {
    flex: 1;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 16px;
    border: 1px solid #1989fa;
    border-radius: 999px;
    background: #fff;
    font-size: 14px;
    line-height: 20px;
    color: #1989fa;
  }

  .invalid-popup-loading {
    text-align: center;
    padding: 20px 0;
  }
}
</style>
